import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { showSuccess, showError } from '@/lib/toast';
import { formatDate, formatCurrency } from '@/lib/utils';
import {
  ArrowLeft, ExternalLink, Check, Loader2, Server, Cpu, Globe,
  DollarSign, Activity, Shield, CheckCircle2, Copy,
  Eye, EyeOff, Play, Square, RotateCw, Trash2, Key, AlertCircle
} from 'lucide-react';
import { useState } from 'react';

// Status configuration
const statusConfig: Record<string, { color: string; badge: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  running: { color: 'bg-emerald-500', badge: 'success' },
  stopped: { color: 'bg-gray-400', badge: 'secondary' },
  provisioning: { color: 'bg-yellow-500', badge: 'warning' },
  error: { color: 'bg-red-500', badge: 'destructive' },
  starting: { color: 'bg-blue-500', badge: 'secondary' },
  stopping: { color: 'bg-orange-500', badge: 'warning' },
  paused: { color: 'bg-amber-500', badge: 'warning' },
};

// Device Pairing Component
function DevicePairing({ instanceId, hostingType }: { instanceId: string; hostingType: string }) {
  const [isApproving, setIsApproving] = useState(false);

  const { data: devices, refetch, isError, isLoading } = useQuery({
    queryKey: ['devices', instanceId],
    queryFn: async () => {
      const { data } = await api.get(`/deploy/${instanceId}/devices`);
      console.log('📱 Device API Response:', data);
      return data;
    },
    refetchInterval: 5000,
    retry: 3,
    enabled: hostingType === 'managed', // Only fetch for managed instances
  });

  const handleApproveAll = async () => {
    if (isApproving) return;
    setIsApproving(true);
    try {
      const { data } = await api.post(`/deploy/${instanceId}/approve-devices`);
      showSuccess(`Approved ${data.approved} device(s)`);
      await refetch();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to approve devices');
    } finally {
      setIsApproving(false);
    }
  };

  // Don't show anything for BYOS instances
  if (hostingType !== 'managed') {
    console.log('⚠️ Device pairing only available for managed instances');
    return null;
  }

  if (isLoading) {
    console.log('⏳ Loading device pairing status...');
    return null; // Don't show loading state
  }

  if (isError) {
    console.error('❌ Failed to fetch device pairing status');
    return (
      <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20">
        <CardContent className="flex items-center gap-3 p-4">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <p className="text-sm text-red-800 dark:text-red-200">
            Unable to check device pairing status. Please refresh the page.
          </p>
        </CardContent>
      </Card>
    );
  }

  console.log('🔍 Device pairing data:', devices);

  if (!devices?.pending?.length) {
    console.log('✅ No pending devices found');
    return null;
  }

  console.log(`🚨 Found ${devices.pending.length} pending device(s)!`);

  return (
    <Card className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 shrink-0">
              <Shield className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-1">Device Approval Required</h3>
              <p className="text-xs text-muted-foreground mb-3">
                {devices.pending.length} device(s) waiting to connect. Approve to grant access.
              </p>
              {devices.pending.map((device: any, i: number) => (
                <div key={device.requestId || i} className="text-xs font-mono text-amber-700 dark:text-amber-300 bg-white/50 dark:bg-black/20 px-2 py-1 rounded">
                  {device.requestId}
                </div>
              ))}
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleApproveAll}
            disabled={isApproving}
            className="bg-emerald-600 hover:bg-emerald-700 shrink-0"
          >
            {isApproving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Approve All
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function InstanceDetailClean() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [copying, setCopying] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Queries
  const { data: instance, isLoading } = useQuery({
    queryKey: ['instance', id],
    queryFn: async () => {
      const { data } = await api.get(`/instances/${id}`);
      return data;
    },
    enabled: !!id,
    refetchInterval: 10000,
  });

  // Mutations
  const startMutation = useMutation({
    mutationFn: () => api.post(`/instances/${id}/start`),
    onSuccess: () => {
      showSuccess('Instance starting...');
      queryClient.invalidateQueries({ queryKey: ['instance', id] });
    },
    onError: () => showError('Failed to start instance'),
  });

  const stopMutation = useMutation({
    mutationFn: () => api.post(`/instances/${id}/stop`),
    onSuccess: () => {
      showSuccess('Instance stopping...');
      queryClient.invalidateQueries({ queryKey: ['instance', id] });
    },
    onError: () => showError('Failed to stop instance'),
  });

  const restartMutation = useMutation({
    mutationFn: () => api.post(`/instances/${id}/restart`),
    onSuccess: () => {
      showSuccess('Instance restarting...');
      queryClient.invalidateQueries({ queryKey: ['instance', id] });
    },
    onError: () => showError('Failed to restart instance'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/instances/${id}`),
    onSuccess: () => {
      showSuccess('Instance deleted');
      navigate('/dashboard/instances');
    },
    onError: () => showError('Failed to delete instance'),
  });

  const deployMutation = useMutation({
    mutationFn: () => api.post(`/deploy/${id}`),
    onSuccess: () => {
      showSuccess('Deployment started!');
      // Redirect to real-time deployment progress page
      navigate(`/dashboard/deploy/${id}`);
    },
    onError: () => showError('Failed to start deployment'),
  });

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopying(label);
      showSuccess(`${label} copied!`);
      setTimeout(() => setCopying(''), 2000);
    } catch {
      showError('Failed to copy');
    }
  };

  if (isLoading || !instance) {
    return (
      <div className="container max-w-7xl mx-auto p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const status = instance.status;
  const isRunning = status === 'running';
  const isStopped = status === 'stopped';
  const isProvisioning = status === 'provisioning' || status === 'starting' || status === 'stopping';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/instances')} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">{instance.name}</h1>
              <p className="text-sm text-muted-foreground">
                {(instance.hostingType || instance.hosting_type) === 'managed' ? 'Managed / Shared' : 'BYOS (Your Server)'}
                {(instance.keyMode || instance.key_mode) === 'platform_key' ? ' • Platform Key' : ' • Own API Key'}
              </p>
            </div>
          </div>
          <Badge variant={statusConfig[status]?.badge || 'secondary'} className="text-sm px-3 py-1">
            <div className={`w-2 h-2 rounded-full ${statusConfig[status]?.color} mr-2`}></div>
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </div>

        {/* Actions Bar */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {isRunning && instance.openclawUrl && (
                  <Button
                    size="sm"
                    onClick={() => window.open(instance.openclawUrl || instance.openclaw_url, '_blank')}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open Dashboard
                  </Button>
                )}

                {status === 'provisioning' && (
                  <Button
                    size="sm"
                    onClick={() => deployMutation.mutate()}
                    disabled={deployMutation.isPending}
                    className="gap-2 bg-blue-600 hover:bg-blue-700"
                  >
                    {deployMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Starting Deployment...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4" />
                        Deploy Instance
                      </>
                    )}
                  </Button>
                )}

                {status !== 'provisioning' && !isProvisioning && (
                  <>
                    {isRunning && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => stopMutation.mutate()} disabled={stopMutation.isPending}>
                          {stopMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Square className="h-4 w-4 mr-2" />
                          )}
                          Stop
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => restartMutation.mutate()} disabled={restartMutation.isPending}>
                          {restartMutation.isPending ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <RotateCw className="h-4 w-4 mr-2" />
                          )}
                          Restart
                        </Button>
                      </>
                    )}

                    {isStopped && (
                      <Button size="sm" variant="outline" onClick={() => startMutation.mutate()} disabled={startMutation.isPending}>
                        {startMutation.isPending ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Play className="h-4 w-4 mr-2" />
                        )}
                        Start
                      </Button>
                    )}
                  </>
                )}
              </div>

              <Button
                size="sm"
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Destroy
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Device Pairing Alert for Managed */}
        {isRunning && <DevicePairing instanceId={instance.id} hostingType={instance.hostingType || instance.hosting_type} />}

        {/* BYOS Device Pairing Instructions */}
        {isRunning && (instance.hostingType === 'byos' || instance.hosting_type === 'byos') && (
          <Card className="border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 shrink-0">
                  <Shield className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm mb-2">Device Pairing Required?</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    If you see "pairing required" when connecting, SSH into your server and approve devices:
                  </p>
                  <div className="bg-gray-900 rounded-lg p-3 space-y-2">
                    <code className="text-xs text-green-400 font-mono block">
                      ssh {instance.sshUser || instance.ssh_user || 'root'}@{instance.serverIp || instance.server_ip}
                    </code>
                    <code className="text-xs text-green-400 font-mono block">
                      docker exec {instance.containerName || instance.container_name || 'openclaw'} openclaw devices list
                    </code>
                    <code className="text-xs text-green-400 font-mono block">
                      docker exec {instance.containerName || instance.container_name || 'openclaw'} openclaw devices approve {'<request-id>'}
                    </code>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    <strong>Tip:</strong> Copy the Request ID from the device list and paste it in the approve command.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Server IP */}
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <Globe className="h-5 w-5 text-blue-600" />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(instance.serverIp || instance.server_ip, 'Server IP')}
                  className="h-8 w-8 p-0"
                >
                  {copying === 'Server IP' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mb-1">Server IP</p>
              <p className="font-mono text-sm font-semibold">{instance.serverIp || instance.server_ip || '—'}</p>
            </CardContent>
          </Card>

          {/* Server Size */}
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg w-fit mb-2">
                <Server className="h-5 w-5 text-emerald-600" />
              </div>
              <p className="text-xs text-muted-foreground mb-1">Server Size</p>
              <p className="text-sm font-semibold capitalize">{instance.serverSize || instance.server_size || '—'}</p>
            </CardContent>
          </Card>

          {/* AI Model */}
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg w-fit mb-2">
                <Cpu className="h-5 w-5 text-purple-600" />
              </div>
              <p className="text-xs text-muted-foreground mb-1">AI Model</p>
              <p className="text-sm font-semibold">{instance.aiModel || instance.ai_model || '—'}</p>
            </CardContent>
          </Card>

          {/* Monthly Cost */}
          <Card className="shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg w-fit mb-2">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <p className="text-xs text-muted-foreground mb-1">Monthly Cost</p>
              <p className="text-lg font-bold">{formatCurrency(instance.monthlyCostCents || instance.monthly_cost_cents || 0)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Access Credentials */}
        {isRunning && (instance.gatewayPassword || instance.gateway_password || instance.openclawUrl) && (
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                Access Credentials
              </CardTitle>
              <CardDescription>
                Use these credentials to connect to your OpenClaw instance
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Gateway URL */}
              {instance.openclawUrl && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-2">Dashboard URL</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                      {instance.openclawUrl || instance.openclaw_url}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(instance.openclawUrl || instance.openclaw_url, 'URL')}
                    >
                      {copying === 'URL' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {/* Gateway Password */}
              {(instance.gatewayPassword || instance.gateway_password) && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-2">Gateway Password</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono">
                      {showPassword ? (instance.gatewayPassword || instance.gateway_password) : '••••••••••••••••'}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(instance.gatewayPassword || instance.gateway_password, 'Password')}
                    >
                      {copying === 'Password' ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Additional Details */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Instance Details
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Created</p>
              <p className="text-sm font-medium">{formatDate(instance.createdAt || instance.created_at)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Last Updated</p>
              <p className="text-sm font-medium">{formatDate(instance.updatedAt || instance.updated_at)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Instance ID</p>
              <p className="text-xs font-mono break-all">{instance.id}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Instance</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this instance? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                deleteMutation.mutate();
                setShowDeleteDialog(false);
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
